import { useCallback } from "react";

import { useTranslation } from "next-i18next";

import useMeLazy from "@/Apollo/hooks/useMeLazy";
import useMerchant from "@/hooks/useMerchant";
import addToast from "@/toast/addToast";
import { showModal } from "@/ui/Modal2";

type VerifyModalGuardOptions = {
  phone?: string | null;
  email?: string | null;
};

/**
 * Resolves `true` when the merchant is verified (or verifies in the modal),
 * `false` when the verify modal was closed, and `null` when `Me` couldn't be
 * loaded (a generic error toast is shown).
 */
export const useVerifyAccountModalGuard = () => {
  const { t } = useTranslation();
  const { phone, verified } = useMerchant();
  const [fetchMe] = useMeLazy({ fetchPolicy: "network-only" });

  const guard = useCallback(
    async (options?: VerifyModalGuardOptions) => {
      if (verified) {
        return true;
      }

      // WHO-6172: callers await the login wall first, so `verified` here is
      // from the render before login. Ask the gateway instead of trusting it.
      const { data, error } = await fetchMe();
      const merchant = data?.me?.merchants?.[0];

      if (error || !merchant) {
        addToast(t("common:common_generic_error_message"), {
          appearance: "error",
        });

        return null;
      }

      if (merchant.verified) {
        return true;
      }

      return (await showModal("verify-account", {
        phone: options?.phone || merchant.phone || phone,
      })) as boolean;
    },
    [fetchMe, phone, t, verified]
  );

  return { guard };
};
